import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { clearAllValuesInObj, useJunoCommonFunctions } from '../../../client/Juno/helpers';
import JncEmptyData from '../../../client/Juno/client/components/table/JncEmptyData';
import SavedBankAcountCards from './SavedBankAcountCards';
import AddNewAccountForm from './AddNewAccountForm';
import ActionConfirmModal from '../common/ActionConfirmModal';
import { apiService } from '../../../client/common/apiCallService';
import TransactionStatus from '../TransactionStatus';
import { Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import clsx from 'clsx';
import useWindowWidth from '../../hooks/useWindowWidth';
import { truncateMiddle } from '../../helpers/constant';

export default function SavedBankAcountActions({
    isDashboardPage,
    modalsOnly,
    isAccount,
    isEdit,
    onClose: parentOnClose,
    formData: parentFormData,
    onBack: parentOnBack,
}) {
    const {
        getSavedAccounts,
        saveAccountApi,
        updateAccountApi,
        validateFreezedCurrencies,
        getCountries,
    } = useJunoCommonFunctions();
    const savedAccounts = useSelector(state => state.juno.savedAccounts);
    const api_url = useSelector(state => state.config.api_url);

    const [accountModal, setAccountModal] = useState(isAccount || false);
    const [isEditAccount, setIsEditAccount] = useState(isEdit || false);
    const [isLoading, setIsLoading] = useState(false);
    const clientData = useSelector(state => state.juno.clientData);
    const [errorInputNames, setErrorsInputNames] = useState([]);
    const [countries, setCountries] = useState([]);

    // Delete Modal State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [accountToDelete, setAccountToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Success Modal State
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const { readOnly } = clientData || {};

    useEffect(() => {
        if (clientData) {
            if (!modalsOnly) {
                getSavedAccounts();
            }
            fetchCountries();
        }
    }, [clientData]);

    const fetchCountries = async () => {
        try {
            const countries = await getCountries();
            setCountries(countries?.allCountries);
        } catch (error) {
            console.error('Error fetching countries:', error);
        }
    };

    // Centralized Delete Functions
    const handleDeleteAccount = accountData => {
        setAccountToDelete(accountData);
        setShowDeleteModal(true);
    };

    const confirmDeleteAccount = () => {
        if (!accountToDelete) return;

        setDeleteLoading(true);
        apiService(
            (window.location.hostname === 'localhost' ? api_url : '') +
                '/restapi/delete-client-beneficiary',
            {
                beneficiaryId: accountToDelete._id,
            },
            async data => {
                if (data) {
                    setDeleteLoading(false);
                    setShowDeleteModal(false);
                    setAccountToDelete(null);
                    onSuccessDelete();
                }
            },
            err => {
                console.log(err);
                setDeleteLoading(false);
            },
        );
    };

    const cancelDeleteAccount = () => {
        setShowDeleteModal(false);
        setAccountToDelete(null);
    };

    function openAccountModal(data) {
        if (data && data._id) {
            setIsEditAccount(true);

            setFormData({
                ...data,
                currency: Array.isArray(data.currency) ? data.currency : [data.currency],
            });

            setAccountModal(true);
        }
    }

    function addNewAccount() {
        setIsEditAccount(false);
        setFormData({
            beneficiaryName: '',
            beneficiaryAddress: '',
            bankName: '',
            bankAddress: '',
            beneficiaryAccountNumber: '',
            sortCode: '',
            swift: '',
            iban: '',
            additionalBankDetails: '',
            amount: '',
            reference: '',
            accountNickName: '',
            saveBankAccount: false,
            bankCountry: '',
            beneficiaryCountry: '',
            currency: [],
        });

        setAccountModal(true);
    }

    function hideIsEditAccount() {
        setIsEditAccount(false);
        setFormData(clearAllValuesInObj(formData));
        setErrorsInputNames([]);
    }

    const [formData, setFormData] = useState({
        beneficiaryName: '',
        beneficiaryAddress: '',
        bankName: '',
        bankAddress: '',
        beneficiaryAccountNumber: '',
        sortCode: '',
        swift: '',
        iban: '',
        additionalBankDetails: '',
        amount: '',
        reference: '',
        accountNickName: '',
        saveBankAccount: false,
        bankCountry: '',
        beneficiaryCountry: '',
        currency: [],
        ...(parentFormData ? { ...parentFormData, currency: [parentFormData.currency] } : {}),
    });

    function onSelectCurrency(val) {
        setFormData(x => {
            return {
                ...x,
                currency: val,
            };
        });
        if (val.length > 0) {
            setErrorsInputNames(prev => prev.filter(x => x !== 'currency'));
        }
    }

    function onChangeInput(e) {
        const name = e.target.name;
        let value;
        if (e.target.type === 'checkbox') value = e.target.checked;
        else value = e.target.value;
        setFormData(x => {
            return {
                ...x,
                [name]: value,
            };
        });
        if (
            (name === 'accountNickName' && value && errorInputNames.includes('accountNickName')) ||
            (name === 'beneficiaryName' && value && errorInputNames.includes('beneficiaryName'))
        ) {
            setErrorsInputNames(prev => prev.filter(x => x !== name));
        }
    }

    function onCloseHandler() {
        if (!isLoading) {
            setAccountModal(false);
            setTimeout(() => {
                resetUseStates();
            }, 300);
        }
    }

    function resetUseStates() {
        setIsEditAccount(false);
        setFormData(clearAllValuesInObj(formData));
        setErrorsInputNames([]);
    }

    function onSuccessDelete() {
        getSavedAccounts();
        setFormData(clearAllValuesInObj(formData));
        setIsEditAccount(false);
        setAccountModal(false);

        if (parentOnClose) {
            parentOnClose();
        }
    }

    function validateFields() {
        let updatedErrors = [];
        setErrorsInputNames(prev => {
            if (!formData.accountNickName || formData.accountNickName.trim() === '') {
                updatedErrors = [...updatedErrors, 'accountNickName'];
            }
            if (!formData.currency[0]) {
                updatedErrors = [...updatedErrors, 'currency'];
            }
            if (!formData.beneficiaryName || formData.beneficiaryName.trim() === '') {
                updatedErrors.push('beneficiaryName');
            }
            if (!formData.bankCountry) {
                updatedErrors.push('bankCountry');
            }
            if (!formData.beneficiaryCountry) {
                updatedErrors.push('beneficiaryCountry');
            }
            return updatedErrors;
        });
        return updatedErrors;
    }

    function onSaveAccountSuccess() {
        getSavedAccounts();
        setFormData({
            beneficiaryName: '',
            beneficiaryAddress: '',
            bankName: '',
            bankAddress: '',
            beneficiaryAccountNumber: '',
            sortCode: '',
            swift: '',
            iban: '',
            additionalBankDetails: '',
            amount: '',
            reference: '',
            accountNickName: '',
            saveBankAccount: false,
            currency: [],
            bankCountry: '',
            beneficiaryCountry: '',
        });
        setAccountModal(false);
        setIsLoading(false);
        setErrorsInputNames([]);
        setSuccessMessage('Bank account added successfully');
        // Show success modal
        setShowSuccessModal(true);
    }

    function onSaveAccountError() {
        setIsLoading(false);
        setErrorMessage('Failed to add bank account');
        setShowErrorModal(true);
    }

    function onUpdateAccountSuccess() {
        getSavedAccounts();
        setSuccessMessage('Bank account updated successfully');
        hideIsEditAccount();
        setAccountModal(false);
        setIsLoading(false);
        // Show success modal
        setShowSuccessModal(true);

        if (parentOnClose) {
            parentOnClose();
        }
    }

    function onUpdateAccountError() {
        setIsLoading(false);
        setErrorMessage('Failed to add bank account');
        setShowErrorModal(true);
        if (parentOnClose) {
            parentOnClose();
        }
    }

    function saveAccount() {
        validateFields();
        if (
            !formData.accountNickName?.trim() ||
            !formData.currency[0]?.trim() ||
            !formData.bankCountry?.trim() ||
            !formData.beneficiaryCountry?.trim() ||
            !formData.beneficiaryName?.trim()
        ) {
            return;
        }
        setIsLoading(true);
        if (!isEditAccount) {
            saveAccountApi(
                formData,
                onSaveAccountSuccess,
                onSaveAccountError,
                // setFailedErr,
            );
        } else {
            updateAccountApi(formData, onUpdateAccountSuccess, onUpdateAccountError);
        }
    }

    const handleCloseSuccessModal = () => {
        setShowSuccessModal(false);
    };

    const handleCloseErrorModal = () => {
        setShowErrorModal(false);
    };

    const windowWidth = useWindowWidth();

    return (
        <div className=" border !border-section-br rounded-lg lg:!m-1 lg:!mt-[3px]">
            {!modalsOnly ? (
                <section>
                    {isDashboardPage && (
                        <SavedBankAcountCards
                            addNewAccount={addNewAccount}
                            openAccountModal={openAccountModal}
                            savedAccounts={savedAccounts}
                            onDeleteAccount={handleDeleteAccount}
                        />
                    )}

                    {/* Only render this section on /client/accounts */}
                    {!isDashboardPage && (
                        <div className="">
                            <div className="flex h-[52px] px-4 py-[14px] justify-between items-start rounded-b-lg  mb-1 bg-filterPannel-bg lg:hidden">
                                <Typography
                                    variant="subtitle1"
                                    className="text-section-title !text-sm !font-semibold !font-inter">
                                    SAVED BANK ACCOUNTS
                                    {savedAccounts.length ? (
                                        <span
                                            className="text-center w-4 h-4 flex-col bg-gray-200 dark:bg-[#51525C]
                              !text-primary font-inter text-[14px] font-semibold ml-1 px-2 py-1 rounded-md">
                                            {savedAccounts.length}
                                        </span>
                                    ) : null}
                                </Typography>
                                {savedAccounts.length > 0 ? (
                                    <Add
                                        className={clsx(
                                            'text-balanceCard-manage cursor-pointer',
                                            // readOnly && 'opacity-50',
                                        )}
                                        onClick={readOnly ? null : addNewAccount}
                                    />
                                ) : null}
                            </div>

                            <SavedBankAcountCards
                                addNewAccount={addNewAccount}
                                openAccountModal={openAccountModal}
                                savedAccounts={savedAccounts}
                                onDeleteAccount={handleDeleteAccount}
                                accountsPage={true}
                            />
                        </div>
                    )}
                </section>
            ) : null}

            {accountModal && (
                <AddNewAccountForm
                    open={accountModal}
                    isEdit={isEditAccount}
                    onBack={() => {
                        if (parentOnBack) {
                            parentOnBack();
                        }
                        setAccountModal(false);
                        // setIsNewAccountComponent(false)
                        resetUseStates();
                    }}
                    title={isEditAccount ? 'Edit Account' : 'Add Bank Account'}
                    onClose={parentOnClose || onCloseHandler}
                    buttonText={isEditAccount ? 'SAVE' : 'ADD BANK ACCOUNT'}
                    // Pass all necessary props for form functionality
                    formData={formData}
                    setFormData={setFormData}
                    errorInputNames={errorInputNames}
                    onChangeInput={onChangeInput}
                    onSelectCurrency={onSelectCurrency}
                    countries={countries}
                    saveAccount={saveAccount}
                    isLoading={isLoading}
                    setErrorsInputNames={setErrorsInputNames}
                    onDeleteAccount={handleDeleteAccount}
                />
            )}

            {/* Centralized Delete Confirmation Modal */}
            {showDeleteModal && accountToDelete && (
                <ActionConfirmModal
                    open={showDeleteModal}
                    heading={'Delete Bank Account!'}
                    subHeading={'Are you sure you want to delete the bank account?'}
                    onCancel={cancelDeleteAccount}
                    onConfirm={confirmDeleteAccount}
                    loading={deleteLoading}
                    type={'delete'}
                    actionButtonText="delete"
                    body={() => (
                        <div>
                            <p className="text-[#18181B] dark:text-[#FFF] text-center text-base font-semibold leading-6 uppercase">
                                {/* {accountToDelete.accountNickName} */}
                                {truncateMiddle(accountToDelete.accountNickName, windowWidth >= 640 ? 20 : 10)}
                            </p>
                            <p className="text-[#51525C] dark:text-[#D1D1D6] text-center text-sm font-medium leading-5 uppercase">
                            {truncateMiddle(accountToDelete.beneficiaryName, windowWidth >= 640 ? 20 : 10)}
                            </p>
                        </div>
                    )}
                />
            )}

            {/* Success Modal */}
            {showSuccessModal && (
                <TransactionStatus
                    status="success"
                    description={successMessage}
                    buttonText="CLOSE"
                    onClick={handleCloseSuccessModal}
                />
            )}

            {/* Error Modal */}
            {showErrorModal && (
                <TransactionStatus
                    status="error"
                    description={errorMessage}
                    buttonText="CLOSE"
                    onClick={handleCloseErrorModal}
                />
            )}
        </div>
    );
}
